import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2, Printer } from "react-feather";
import Select from "react-select";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import confirmDelete from "../components/confirmDelete";
import { fetchQuotations, deleteQuotationThunk } from "../../core/redux/slices/quotationSlice";

const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "Local", label: "Local" },
  { value: "Arab", label: "Arab" },
];

const STATUS_BADGE = { Active: "success", Cancelled: "danger" };

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const QuotationList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, loading } = useSelector((s) => s.quotations);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    dispatch(fetchQuotations());
  }, [dispatch]);

  const data = useMemo(() => {
    const q = search.toLowerCase();
    return list.filter((rec) => {
      const matchSearch = !q || rec.patientName?.toLowerCase().includes(q) || rec.mobileNumber?.includes(q);
      const matchType = !typeFilter || rec.quoteType === typeFilter;
      return matchSearch && matchType;
    });
  }, [list, search, typeFilter]);

  const columns = [
    { title: "Quotation #", dataIndex: "quotationNo", width: 170, render: (v, rec) => v || rec.id },
    { title: "Patient", dataIndex: "patientName", sorter: (a, b) => (a.patientName || "").localeCompare(b.patientName || "") },
    { title: "Mobile", dataIndex: "mobileNumber", width: 120 },
    { title: "Date", dataIndex: "quotationDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    {
      title: "Type", dataIndex: "quoteType", width: 100,
      render: (v) => <span className={`badge bg-${v === "Arab" ? "info" : "secondary"}`}>{v}</span>,
    },
    {
      title: "Total Amount", dataIndex: "totalAmount", align: "right", width: 130,
      render: fmt,
      sorter: (a, b) => (a.totalAmount || 0) - (b.totalAmount || 0),
    },
    {
      title: "Status", dataIndex: "status", width: 100,
      render: (v) => <span className={`badge bg-${STATUS_BADGE[v] || "secondary"}`}>{v}</span>,
    },
    {
      title: "Action", dataIndex: "action", width: 120,
      render: (_, rec) => (
        <div className="edit-delete-action d-flex">
          <button className="me-2 p-2 btn btn-sm" title="Print" onClick={() => window.open(`/quotations/${rec.id}/print`, "_blank")}>
            <Printer size={15} />
          </button>
          <button className="me-2 p-2 btn btn-sm" title="View/Edit" onClick={() => navigate(`/quotations/${rec.id}/edit`)}>
            <Edit size={15} />
          </button>
          <button className="p-2 btn btn-sm text-danger"
            onClick={() => confirmDelete(() => dispatch(deleteQuotationThunk(rec.id)), `quotation for ${rec.patientName}`)}>
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Treatment Quotations" subtitle="Manage patient treatment quotations" onAdd={() => navigate("/quotations/new")} addLabel="New Quotation" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div style={{ minWidth: 160 }}>
                <Select classNamePrefix="react-select" options={TYPE_OPTIONS}
                  value={TYPE_OPTIONS.find((o) => o.value && o.value === typeFilter) || null}
                  placeholder="All Types"
                  isClearable
                  onChange={(opt) => setTypeFilter(opt?.value || "")} />
              </div>
              <div className="search-input flex-grow-1">
                <input type="text" placeholder="Search by patient..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationList;
